import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import type { MatchEvent, Side, Square } from '../../../engine';
import type { HorrorId } from '../../../engine/ledger/ids';
import { itemName } from '../../../app/describe';
import { sounds, type SoundName } from '../../../app/sound';
import { BrokerEyes, HandShape, type HandKind } from './Broker';

/**
 * The bosses' powers after Night III, shown happening (LEDGER.MD §13.1,
 * docs/LONG_NIGHT_ART_DIRECTION.md §11.19). The engine has already moved the pawns; here the
 * board holds them where they stood until his hands, the lightning or the dark reach them, and
 * then they go. Every power is a short timeline of beats in whole steps. The match waits while
 * one plays. With reduced motion the pawns simply stand where they are now, and the power's name
 * is shown for a moment.
 */

export type HorrorEvent = Extract<MatchEvent, { type: 'Horror' }>;

/** What a power being shown asks of the board and the screen. */
export interface HorrorFrame {
  /** Pawns drawn where they stood (pawn id → square), until the moment they move. */
  readonly heldAt: Readonly<Record<string, Square>> | null;
  /** Pawns lifted off the board in his fingers. */
  readonly lifting: readonly string[];
  /** Classes on the board's frame: turning it, carrying, sliding slowly, or cutting. */
  readonly stage: string;
  /** Over the squares (clipped to the board): scorch, smudges, a flash. */
  readonly inner: ReactNode;
  /** Over the whole board and above it: his hands, the lightning, the scratches. */
  readonly outer: ReactNode;
  /** Over the whole screen: the dark, the flash, the power's name. */
  readonly screen: ReactNode;
}

interface Beat {
  readonly at: number;
  readonly name: string;
  readonly sound?: SoundName;
}

/** Each power's beats (ms from its start); 'move' is when the held pawns are let go. */
const TIMELINES: Readonly<Record<HorrorId, readonly Beat[]>> = {
  storm: [
    { at: 0, name: 'gather' },
    { at: 520, name: 'strike', sound: 'thunder' },
    { at: 640, name: 'move' },
    { at: 1000, name: 'after' },
    { at: 2300, name: 'done' },
  ],
  switch: [
    { at: 0, name: 'up' },
    { at: 40, name: 'reach', sound: 'breath' },
    { at: 900, name: 'lift', sound: 'creak' },
    { at: 1150, name: 'move' },
    { at: 2250, name: 'drop', sound: 'thud' },
    { at: 2500, name: 'leave' },
    { at: 3200, name: 'done' },
  ],
  'turned-table': [
    { at: 0, name: 'up' },
    { at: 40, name: 'reach', sound: 'breath' },
    { at: 800, name: 'turn', sound: 'creak' },
    { at: 2300, name: 'move' },
    { at: 2350, name: 'leave', sound: 'thud' },
    { at: 3000, name: 'done' },
  ],
  'lights-out': [
    { at: 0, name: 'fall', sound: 'snuff' },
    { at: 650, name: 'dark' },
    { at: 900, name: 'eyes', sound: 'breath' },
    { at: 1400, name: 'move' },
    { at: 2300, name: 'rise', sound: 'match' },
    { at: 3000, name: 'done' },
  ],
  'blind-eye': [
    { at: 0, name: 'up' },
    { at: 40, name: 'slash', sound: 'scratch' },
    { at: 700, name: 'fall' },
    { at: 1700, name: 'done' },
  ],
  'clean-slate': [
    { at: 0, name: 'up' },
    { at: 40, name: 'reach', sound: 'breath' },
    { at: 750, name: 'sweep', sound: 'curtain' },
    { at: 820, name: 'move' },
    { at: 2000, name: 'leave' },
    { at: 2600, name: 'done' },
  ],
};

/** A square's centre on the board as the viewer sees it, in % of the board. */
function spot(sq: Square, viewer: Side): { left: number; top: number } {
  const col = viewer === 'S' ? sq.x : 5 - sq.x;
  const row = viewer === 'S' ? 5 - sq.y : sq.y;
  return { left: ((col + 0.5) * 100) / 6, top: ((row + 0.5) * 100) / 6 };
}

/** One of his hands at a point of the board (its fingertips there), or lifted out of sight. */
function Hand({
  kind,
  at,
  up = false,
  slow = false,
}: {
  kind: HandKind;
  at: { left: number; top: number };
  up?: boolean;
  slow?: boolean;
}) {
  return (
    <div
      className={`hz-hand is-${kind} ${up ? 'is-up' : ''} ${slow ? 'is-slow' : ''}`}
      style={{ left: `${at.left}%`, top: `${at.top}%` } as CSSProperties}
    >
      <HandShape kind={kind} />
    </div>
  );
}

/** A bolt of lightning in whole pixels, from far above the board down to a square. */
function Bolt({ to }: { to: { left: number; top: number } }) {
  // A fixed zigzag (no randomness on screen): down in steps, each a little to one side.
  const h = 60;
  const points: [number, number][] = [[10, 0]];
  const sway = [5, -6, 3, -5, 6, -3, 4, -1];
  for (let i = 1; i <= sway.length; i++) points.push([10 + sway[i - 1]!, (h * i) / sway.length]);
  points[points.length - 1] = [10, h];
  const d = points.map(([x, y], i) => `${i ? 'L' : 'M'}${x} ${y}`).join(' ');
  return (
    <svg
      className="hz-bolt"
      viewBox={`0 0 20 ${h}`}
      preserveAspectRatio="none"
      shapeRendering="crispEdges"
      style={{ left: `${to.left}%`, height: `calc(${to.top}% + 160%)` } as CSSProperties}
    >
      <path d={d} className="hz-bolt-glow" />
      <path d={d} className="hz-bolt-core" />
    </svg>
  );
}

/** A mark on a square: scorch, smudge or a scratch's end. */
const Mark = ({ kind, at }: { kind: string; at: { left: number; top: number } }) => (
  <span
    className={`hz-mark is-${kind}`}
    style={{ left: `${at.left}%`, top: `${at.top}%` } as CSSProperties}
  />
);

/** The board's half that is dark under Blind Eye, as the viewer sees it: 'left' or 'right'. */
export function shownHalf(dark: 'left' | 'right' | undefined, viewer: Side): 'left' | 'right' {
  const left = dark !== 'right';
  return viewer === 'S' ? (left ? 'left' : 'right') : left ? 'right' : 'left';
}

interface Show {
  readonly event: HorrorEvent;
  readonly beat: string;
}

/**
 * Plays the powers that arrive with the latest action, one after another. `ready` is false while
 * something else holds the screen (a score being counted, the round closing): a power waits for
 * it, its pawns already held where they stood.
 */
export function useHorrors(
  events: readonly MatchEvent[] | undefined,
  key: number,
  viewer: Side,
  reduceMotion: boolean,
  ready: boolean,
): { frame: HorrorFrame | null; busy: boolean } {
  const [queue, setQueue] = useState<HorrorEvent[]>([]);
  const [show, setShow] = useState<Show | null>(null);
  const [held, setHeld] = useState<Record<string, Square> | null>(null);
  const [afterglow, setAfterglow] = useState<{ marks: ReactNode; until: number } | null>(null);
  // A new action: its powers join the queue, and the pawns they moved are held where they stood
  // (adjusted while rendering, so the board never shows them in their new places first).
  const [seen, setSeen] = useState(key);
  if (seen !== key) {
    setSeen(key);
    const arrived = (events ?? []).filter((e): e is HorrorEvent => e.type === 'Horror');
    if (arrived.length > 0) {
      setQueue((q) => [...q, ...arrived]);
      if (!reduceMotion)
        setHeld((h) => {
          const next = { ...(h ?? {}) };
          // The first power's start squares: a later one's moves start where the earlier ended.
          for (const e of [...arrived].reverse()) for (const m of e.moves) next[m.pawnId] = m.from;
          return next;
        });
    }
  }

  // The next power starts once the screen is free. Its timers live in a ref: only leaving the
  // match cuts a power short.
  const running = useRef(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const [finished, setFinished] = useState(0);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  useEffect(() => {
    if (running.current || !ready || queue.length === 0) return;
    running.current = true;
    const event = queue[0]!;
    const end = () => {
      running.current = false;
      setShow(null);
      setFinished((n) => n + 1);
    };
    // The head of the queue is this power; any that arrive meanwhile stay behind it.
    timers.current.push(setTimeout(() => setQueue((q) => q.slice(1)), 0));
    if (reduceMotion) {
      timers.current.push(setTimeout(() => setShow({ event, beat: 'still' }), 0));
      timers.current.push(setTimeout(end, 1600));
      return;
    }
    for (const b of TIMELINES[event.horror])
      timers.current.push(
        setTimeout(() => {
          if (b.sound) sounds.play(b.sound);
          if (b.name === 'move')
            setHeld((h) => {
              if (!h) return h;
              const next = { ...h };
              for (const m of event.moves) delete next[m.pawnId];
              return Object.keys(next).length ? next : null;
            });
          if (b.name !== 'done') {
            setShow({ event, beat: b.name });
            return;
          }
          if (event.horror === 'lights-out' && event.moves.length)
            setAfterglow({
              marks: event.moves.map((m) => (
                <Mark key={m.pawnId} kind="smudge" at={spot(m.to, viewer)} />
              )),
              until: Date.now() + 4200,
            });
          if (event.horror === 'storm' && event.at)
            setAfterglow({
              marks: <Mark kind="scorch" at={spot(event.at, viewer)} />,
              until: Date.now() + 2600,
            });
          end();
        }, b.at),
      );
  }, [queue, ready, reduceMotion, finished, viewer]);

  useEffect(() => {
    if (!afterglow) return;
    const id = setTimeout(() => setAfterglow(null), Math.max(0, afterglow.until - Date.now()));
    return () => clearTimeout(id);
  }, [afterglow]);

  const frame = show
    ? frameOf(show, viewer, held, afterglow?.marks ?? null)
    : held || afterglow
      ? {
          heldAt: held,
          lifting: [],
          stage: '',
          inner: afterglow?.marks ?? null,
          outer: null,
          screen: null,
        }
      : null;
  return { frame, busy: show !== null || queue.length > 0 };
}

function frameOf(
  { event: e, beat }: Show,
  viewer: Side,
  heldAt: Record<string, Square> | null,
  marks: ReactNode,
): HorrorFrame {
  const name = (
    <p className={`hz-name is-${e.horror} ${beat === 'still' ? 'is-still' : ''}`}>
      {itemName(`rule.${e.horror}`)}
    </p>
  );
  const base: HorrorFrame = {
    heldAt,
    lifting: [],
    stage: `is-horror is-${e.horror} beat-${beat}`,
    inner: marks,
    outer: null,
    screen: name,
  };
  if (beat === 'still') return { ...base, heldAt: null, stage: '' };
  const moves = e.moves;
  switch (e.horror) {
    case 'storm': {
      const at = e.at ? spot(e.at, viewer) : null;
      const striking = beat === 'strike' || beat === 'move';
      return {
        ...base,
        stage: `${base.stage} is-knock`,
        outer: at && striking ? <Bolt to={at} /> : null,
        inner: (
          <>
            {marks}
            {at && (beat === 'move' || beat === 'after') && <Mark kind="scorch" at={at} />}
          </>
        ),
        screen: (
          <>
            {name}
            <span className={`hz-sky beat-${beat}`} />
            {striking && <span className="hz-flash" />}
          </>
        ),
      };
    }
    case 'switch': {
      const [a, b] = moves;
      if (!a || !b) return base;
      const carried = beat === 'move' || beat === 'drop' || beat === 'leave';
      const lifting = beat === 'lift' || beat === 'move' ? [a.pawnId, b.pawnId] : [];
      return {
        ...base,
        lifting,
        stage: `${base.stage} is-carry`,
        outer: (
          <>
            <Hand
              kind="skeleton"
              at={spot(carried ? a.to : a.from, viewer)}
              up={beat === 'up' || beat === 'leave'}
              slow={beat === 'move'}
            />
            <Hand
              kind="hook"
              at={spot(carried ? b.to : b.from, viewer)}
              up={beat === 'up' || beat === 'leave'}
              slow={beat === 'move'}
            />
          </>
        ),
      };
    }
    case 'turned-table': {
      const up = beat === 'up' || beat === 'leave';
      return {
        ...base,
        // The turn ends where it began, the pawns in their new places: no slide, a cut.
        stage: `${base.stage} ${beat === 'turn' ? 'is-turning' : ''} ${
          beat === 'move' || beat === 'leave' ? 'is-cut' : ''
        }`,
        outer: (
          <>
            <Hand kind="skeleton" at={{ left: 4, top: 52 }} up={up} />
            <Hand kind="hook" at={{ left: 96, top: 52 }} up={up} />
          </>
        ),
      };
    }
    case 'lights-out':
      return {
        ...base,
        stage: `${base.stage} is-cut`,
        screen: (
          <>
            <div className={`hz-blackout beat-${beat}`}>
              <BrokerEyes open={beat === 'eyes' || beat === 'move'} />
            </div>
            {name}
          </>
        ),
      };
    case 'blind-eye': {
      const half = shownHalf(e.dark, viewer);
      const from = half === 'left' ? { left: 6, top: 4 } : { left: 56, top: 4 };
      const to = half === 'left' ? { left: 44, top: 96 } : { left: 94, top: 96 };
      return {
        ...base,
        outer: (
          <>
            <Hand kind="hook" at={beat === 'up' ? from : to} up={beat === 'fall'} slow />
            {beat !== 'up' && (
              <svg
                className={`hz-scratch is-${half}`}
                viewBox="0 0 30 60"
                preserveAspectRatio="none"
                shapeRendering="crispEdges"
              >
                <path d="M2 2 L26 56 M8 1 L29 50 M0 9 L22 59" />
              </svg>
            )}
          </>
        ),
      };
    }
    case 'clean-slate': {
      const across = beat === 'sweep' || beat === 'move' || beat === 'leave';
      const up = beat === 'up' || beat === 'leave';
      return {
        ...base,
        stage: `${base.stage} is-slow`,
        outer: (
          <>
            <Hand kind="skeleton" at={{ left: across ? 104 : -4, top: 40 }} up={up} slow />
            <Hand kind="hook" at={{ left: across ? -4 : 104, top: 88 }} up={up} slow />
          </>
        ),
      };
    }
  }
}

/**
 * Each boss's own air over the whole match, for as long as it sits at the table: rain and far
 * lightning (the Storm), a slow wheel turning behind the board (the Turned Table), a band of
 * mirrored light (the Switch), a lamp that gutters now and then (Lights Out), vision narrowing at
 * the edges (the Blind Eye), chalk dust falling (Clean Slate). None of it moves with reduced
 * motion: the match leaves it out.
 */
export function HorrorAmbience({ horror }: { horror: string }) {
  return (
    <div className={`hz-amb is-${horror}`} aria-hidden="true">
      {horror === 'storm' && (
        <>
          <span className="hz-rain" />
          <span className="hz-rain is-near" />
          <span className="hz-skyflash" />
        </>
      )}
      {horror === 'turned-table' && <span className="hz-wheel" />}
      {horror === 'switch' && <span className="hz-mirror" />}
      {horror === 'lights-out' && <span className="hz-gutter" />}
      {horror === 'blind-eye' && <span className="hz-veil" />}
      {horror === 'clean-slate' && <span className="hz-dust" />}
    </div>
  );
}
